import { ChipPickerDom } from "./chip-picker-dom";
import { ChipPickerRender, type ChipPickerRenderOptions } from "./chip-picker-render";
import { UISymbols } from "../../../constants/ui-symbols";

export interface ExternalSelectionUI {
    setSelectedIds: (ids: string[]) => void;
    setOnSelectionChange: (cb: (ids: string[]) => void) => void;
    open: () => void;
}

export interface ChipPickerHandlerParams {
    chipsEl: HTMLElement;
    openBtnEl?: HTMLElement | null;
    idAttribute?: string;
    removeAction?: string;
    renderOptions?: ChipPickerRenderOptions;
}

export class ChipPickerHandler {
    public chipsEl: HTMLElement;
    public openBtnEl: HTMLElement | null;
    public idAttribute: string;
    public removeAction: string;
    public renderOptions: ChipPickerRenderOptions;
    public selectedIds: Set<string>;
    public labelById: Map<string, string>;
    public onSelectionChange: ((ids: string[]) => void) | null;
    public external: ExternalSelectionUI | null;

    public constructor(params: ChipPickerHandlerParams) {
        this.chipsEl = params.chipsEl;
        this.openBtnEl = params.openBtnEl || null;

        this.idAttribute = String(params.idAttribute || "data-item-id");
        this.removeAction = String(params.removeAction || "remove");
        this.renderOptions = params.renderOptions || {};

        this.selectedIds = this.createSelectedIdsSet();
        this.labelById = this.createLabelMap();
        this.onSelectionChange = this.createSelectionChangeHandler();
        this.external = this.createExternalSelectionUi();
    }

    public createSelectedIdsSet(): Set<string> {
        return new Set();
    }

    public createLabelMap(): Map<string, string> {
        return new Map();
    }

    public createSelectionChangeHandler(): ((ids: string[]) => void) | null {
        return null;
    }

    public createExternalSelectionUi(): ExternalSelectionUI | null {
        return null;
    }

    public setLabelMap(labelById: Map<string, string>): void {
        this.labelById = labelById instanceof Map ? labelById : new Map();
    }

    public getLabelMap(): Map<string, string> {
        return this.labelById;
    }

    public mergeLabelMap(labelById: Map<string, string>): void {
        if (!(labelById instanceof Map)) return;
        if (!(this.labelById instanceof Map)) {
            this.labelById = new Map();
        }
        for (const [id, label] of labelById.entries()) {
            const key = String(id ?? "").trim();
            if (!key) continue;
            const value = String(label ?? "").trim();
            if (!value) continue;
            this.labelById.set(key, value);
        }
    }

    public setSelectedIds(ids: string[]): void {
        this.selectedIds.clear();
        (Array.isArray(ids) ? ids : []).forEach((id) => {
            const value = String(id ?? "").trim();
            if (!value) return;
            this.selectedIds.add(value);
        });
    }

    public getSelectedIds(): string[] {
        return Array.from(this.selectedIds);
    }

    public setOnSelectionChange(callback: ((ids: string[]) => void) | null): void {
        this.onSelectionChange = typeof callback === "function" ? callback : null;
    }

    public setExternalSelectionUI(external: ExternalSelectionUI | null): void {
        this.external = external || null;
    }

    public render(): void {
        const hideUnknownLabels = !!this.renderOptions?.hideUnknownLabels;

        const items: Array<{ id: string; label: string }> = [];
        this.getSelectedIds().forEach((id) => {
            const label = this.labelById.get(id);
            if (!label && hideUnknownLabels) return;

            items.push({
                id,
                label: label || id,
            });
        });

        const hasSelections = this.selectedIds.size > 0;

        if (this.openBtnEl instanceof HTMLElement) {
            this.openBtnEl.classList.toggle("has-items", hasSelections);
        }

        ChipPickerRender.render({
            rootEl: this.chipsEl,
            items,
            options: {
                idAttribute: this.idAttribute,
                removeAction: this.removeAction,
                ...this.renderOptions,
            },
        });
    }

    public initializeUi(rootEl: ParentNode): void {
        ChipPickerRender.setClearButtonsLabel(rootEl, UISymbols.PICKER_REMOVE);
    }

    public clearAll(): void {
        this.selectedIds.clear();
        this.render();
        this.external?.setSelectedIds?.([]);
        this.onSelectionChange?.([]);
    }

    public syncToExternal(): void {
        if (!this.external) return;

        this.external.setSelectedIds(this.getSelectedIds());
        this.external.setOnSelectionChange((ids) => {
            this.setSelectedIds(ids);
            this.render();
            this.onSelectionChange?.(this.getSelectedIds());
        });
    }

    public handleOpenClick(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();

        if (!this.external) return;
        this.syncToExternal();
        this.external.open();
    }

    public handleChipsClick(event: MouseEvent): void {
        const id = ChipPickerDom.getRemoveIdFromClick(event, {
            idAttribute: this.idAttribute,
            removeAction: this.removeAction,
        });
        if (!id) {
            if (!this.external) return;

            event.preventDefault();
            event.stopPropagation();
            this.syncToExternal();
            this.external.open();
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        this.selectedIds.delete(id);
        this.render();
        this.external?.setSelectedIds?.(this.getSelectedIds());
        this.onSelectionChange?.(this.getSelectedIds());
    }
}
